// Audience-aware fetch factory for the web app.
//
// Multiple surfaces (account, admin, helpdesk) need the same machinery:
// attach a Bearer token from localStorage, rotate via /auth/refresh on 401,
// serialize refreshes across every tab and fetcher (refresh tokens are
// single-use server-side — see lib/auth-session.ts), and clear + signal on
// terminal failure. Rather than duplicate the wrapper per surface, each
// surface declares an Audience and binds the factory.
//
// The refresh token now lives in an httpOnly cookie for BOTH audiences
// (`cookieRefresh: true`) instead of localStorage, so it can't be read by XSS and
// so silent rotation actually works (the cookie interceptor moves the refresh
// token out of the login body, which previously left the admin's localStorage key
// empty and broke refresh — #142).
//
// Mirrors the mobile pattern in packages/api-client/src/http.ts.

import { rotateSessionToken, type RefreshAttempt, type RefreshOutcome } from './auth-session'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

export interface Audience {
  /** localStorage key holding the short-lived access JWT. */
  tokenKey: string
  /**
   * localStorage key holding the refresh token, if this audience keeps it in
   * localStorage. `null` means the refresh token isn't in JS-readable storage —
   * either it's a cookie (`cookieRefresh`) or this audience can't rotate.
   */
  refreshKey: string | null
  /**
   * When true, the refresh token is an httpOnly cookie: rotate by POSTing
   * /auth/refresh with `credentials: 'include'` (no body token), and the server
   * sets the rotated cookie on the response.
   */
  cookieRefresh?: boolean
  /** localStorage key for the cached user object — cleared on terminal failure. */
  userKey: string | null
  /** Window event fired after terminal-401 cleanup, so AuthContexts can reset state. */
  signoutEvent: string
  /**
   * Sent as `X-Auth-Audience` on every request so the server scopes this
   * audience to its own refresh cookie (admin vs contractor), preventing two
   * sessions in one browser from cross-rotating (#142).
   */
  audienceHeader?: string
}

export const CONTRACTOR_AUDIENCE: Audience = {
  tokenKey: 'tt_token',
  refreshKey: null,
  cookieRefresh: true,
  userKey: 'tt_user',
  signoutEvent: 'tt:auth:cleared',
}

export const ADMIN_AUDIENCE: Audience = {
  // Admin rotates via the httpOnly refresh cookie now (#142), like the contractor
  // audience — its refresh token is no longer in localStorage (the cookie
  // interceptor strips it from the login body). Admin-audience clients (the api
  // wrapper, adminHelpApi, the Control Tower SSE) all rotate on 401 instead of
  // bouncing to login every ~15 min.
  tokenKey: 'tt_admin_token',
  refreshKey: null,
  cookieRefresh: true,
  userKey: 'tt_admin_user',
  signoutEvent: 'tt:admin-auth:cleared',
  audienceHeader: 'admin',
}

export interface AuthFetcher {
  /** JSON helper: throws on non-2xx, returns parsed body (undefined for 204). */
  api: <T>(path: string, options?: RequestInit) => Promise<T>
  /**
   * Raw Response helper for non-JSON consumers (e.g. SSE, blob downloads).
   * Still rotates on 401 + retries once before returning. Caller is responsible
   * for checking `res.ok` and consuming the body.
   */
  fetchWithAuth: (path: string, options?: RequestInit) => Promise<Response>
}

// One POST /auth/refresh for `audience`. Never throws and never writes storage:
// rotateSessionToken decides what, if anything, gets stored.
async function postRefresh(audience: Audience): Promise<RefreshAttempt> {
  const cookieRefresh = !!audience.cookieRefresh
  const refreshToken = audience.refreshKey ? localStorage.getItem(audience.refreshKey) : null
  if (!cookieRefresh && !refreshToken) return { ok: false, transient: false }

  let res: Response
  try {
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(audience.audienceHeader ? { 'X-Auth-Audience': audience.audienceHeader } : {}),
      },
      // Cookie audience: send the httpOnly refresh cookie, no body token.
      credentials: 'include',
      body: JSON.stringify(cookieRefresh ? {} : { refreshToken }),
    })
  } catch {
    // Offline / network failure: says nothing about the session.
    return { ok: false, transient: true }
  }
  // 429 and 5xx are the server's trouble, not a verdict on the refresh token.
  if (!res.ok) return { ok: false, transient: res.status === 429 || res.status >= 500 }

  // A 2xx already rotated the cookie, so an unusable body is no verdict either:
  // the next 401 refreshes off the new cookie.
  let json: { accessToken?: unknown; refreshToken?: unknown }
  try {
    json = await res.json()
  } catch {
    return { ok: false, transient: true }
  }
  if (typeof json.accessToken !== 'string') return { ok: false, transient: true }
  return {
    ok: true,
    accessToken: json.accessToken,
    // Cookie audience: the rotated refresh token comes back as a Set-Cookie,
    // not in the body. localStorage audience: hand it back to be persisted.
    ...(!cookieRefresh && typeof json.refreshToken === 'string' ? { refreshToken: json.refreshToken } : {}),
  }
}

/**
 * Rotate `audience`'s access token after a request sent with `staleToken` got
 * a 401: one POST per expiry across every tab, reusing another tab's rotation
 * when there is one (lib/auth-session.ts). Shared by every fetcher bound to the
 * audience and by the admin wrapper in modules/admin/api.ts.
 */
export function refreshAccessToken(
  audience: Audience,
  staleToken: string | null,
): Promise<RefreshOutcome> {
  return rotateSessionToken(audience, staleToken, () => postRefresh(audience))
}

export function createAuthFetcher(audience: Audience): AuthFetcher {
  function readToken(): string | null {
    if (typeof window === 'undefined') return null
    return localStorage.getItem(audience.tokenKey)
  }

  function readRefreshToken(): string | null {
    if (typeof window === 'undefined' || !audience.refreshKey) return null
    return localStorage.getItem(audience.refreshKey)
  }

  // Whether a 401 should attempt a rotation: the cookie audience always can
  // (the cookie is sent automatically); a localStorage audience only if it has
  // a stored refresh token.
  function canRefresh(): boolean {
    return !!audience.cookieRefresh || !!readRefreshToken()
  }

  function clearStoredAuth(): void {
    if (typeof window === 'undefined') return
    localStorage.removeItem(audience.tokenKey)
    if (audience.refreshKey) localStorage.removeItem(audience.refreshKey)
    if (audience.userKey) localStorage.removeItem(audience.userKey)
    window.dispatchEvent(new Event(audience.signoutEvent))
  }

  function doFetch(
    path: string,
    options: RequestInit | undefined,
    token: string | null,
  ): Promise<Response> {
    // A FormData body (memory uploads) must not carry the JSON default: the
    // browser sets multipart/form-data with its boundary only when no
    // Content-Type is present. SSE callers override it via options.headers.
    const isFormBody = typeof FormData !== 'undefined' && options?.body instanceof FormData
    const headers: Record<string, string> = {
      ...(isFormBody ? {} : { 'Content-Type': 'application/json' }),
      ...(audience.audienceHeader ? { 'X-Auth-Audience': audience.audienceHeader } : {}),
      ...(options?.headers as Record<string, string> | undefined),
    }
    if (token) headers.Authorization = `Bearer ${token}`
    // include credentials so the httpOnly auth cookies ride every request.
    return fetch(`${API_BASE}${path}`, { credentials: 'include', ...options, headers })
  }

  async function fetchWithAuth(path: string, options?: RequestInit): Promise<Response> {
    const token = readToken()
    let res = await doFetch(path, options, token)

    if (res.status === 401 && canRefresh()) {
      const outcome = await refreshAccessToken(audience, token)
      if (outcome.status === 'rotated') {
        // /auth/refresh just vouched for the session, so a 401 on the retry is
        // the endpoint's own answer (a wrong current password, a failed passkey
        // check), not a session verdict. Return it without clearing — clearing
        // signs out every tab. A reused token that has since expired rotates on
        // the next request.
        res = await doFetch(path, options, outcome.token)
      } else if (outcome.status === 'signed-out') {
        clearStoredAuth()
      }
      // 'unavailable': /auth/refresh gave no verdict (offline, 5xx, or another
      // tab stuck mid-rotation). Return the 401 and keep the session — the
      // next request tries again — instead of signing every tab out.
    } else if (res.status === 401) {
      // No refresh capability → terminal.
      clearStoredAuth()
    }

    return res
  }

  async function api<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetchWithAuth(path, options)
    if (res.status === 204) return undefined as T
    if (!res.ok) {
      // Surface the server's human message, not the raw JSON body — most call
      // sites toast `(e as Error).message`, so a bare `res.text()` leaked
      // `{"message":[...],"statusCode":400}` to the user (#181). Parse the
      // Nest error shape ({ message: string | string[] }) and fall back to a
      // generic line. `status` is attached for callers that branch on it.
      const text = await res.text()
      let message = `Something went wrong (${res.status})`
      try {
        const body = JSON.parse(text)
        if (Array.isArray(body?.message)) message = body.message.join('. ')
        else if (typeof body?.message === 'string') message = body.message
        else if (typeof body?.error === 'string') message = body.error
      } catch {
        if (text && text.length < 200) message = text
      }
      const err = new Error(message) as Error & { status?: number }
      err.status = res.status
      throw err
    }
    return res.json()
  }

  return { api, fetchWithAuth }
}
